import { CheckVerified02 } from "@untitledui/icons";
import { useEffect } from "react";
import { usePagination } from "@/api/pagination";
import { useProductUpdates } from "@/api/product-updates";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Button } from "@/components/base/buttons/button";
import { useCache } from "@/hooks/use-cache";
import { useMerchant } from "@/providers/MerchantProvider";
import { formatShortDate } from "@/utils/date-conversion";

const RichText = ({ text }: { text: string }) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return (
    <>
      {parts.map((part) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <span key={part} className="font-semibold text-primary">
              {part.slice(2, -2)}
            </span>
          );
        }
        return <span key={part}>{part}</span>;
      })}
    </>
  );
};

const renderCopy = (copy: string) => {
  const lines = copy.split("\n");
  const elements: React.ReactNode[] = [];
  let currentList: React.ReactNode[] = [];

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="flex flex-col gap-1 pl-4">
          {currentList}
        </ul>,
      );
      currentList = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    if (trimmed.startsWith("- ")) {
      const content = trimmed.slice(2);
      const isBoldOnly =
        content.startsWith("**") && content.endsWith("**") && content.indexOf("**", 2) === content.length - 2;

      if (isBoldOnly) {
        flushList();
        elements.push(
          <h4 key={i} className="text-md font-semibold text-primary pt-2 first:pt-0">
            {content.slice(2, -2)}
          </h4>,
        );
      } else {
        currentList.push(
          <li key={i} className="flex gap-2 text-md text-tertiary leading-relaxed">
            <span className="text-quaternary select-none">•</span>
            <span>
              <RichText text={content} />
            </span>
          </li>,
        );
      }
    } else if (trimmed === "") {
      flushList();
    } else {
      flushList();
      elements.push(
        <p key={i} className="text-md text-tertiary leading-relaxed">
          <RichText text={trimmed} />
        </p>,
      );
    }
  }

  flushList();
  return elements;
};

const isExternalUrl = (url: string) => /^https?:\/\//.test(url) || url.startsWith("//");

const UpdateLink = ({ url, text, merchantId }: { url: string; text: string; merchantId: string | null }) => {
  if (isExternalUrl(url)) {
    return (
      <Button color="link-color" size="sm" href={url} target="_blank" rel="noopener noreferrer">
        {text}
      </Button>
    );
  }

  const internalHref = merchantId ? `/merchant/${merchantId}${url}` : url;
  return (
    <Button color="link-color" size="sm" href={internalHref}>
      {text}
    </Button>
  );
};

type ProductUpdate = {
  id: string;
  title: string;
  copy: string;
  thumbnailUrl: string | null;
  linkText?: string | null;
  linkUrl?: string | null;
  publishedAt: string | null;
};

const TimelineEntry = ({
  update,
  isLast,
  merchantId,
}: {
  update: ProductUpdate;
  isLast: boolean;
  merchantId: string | null;
}) => {
  const showDivider = !isLast && !update.thumbnailUrl;

  return (
    <div id={update.id} className="relative grid grid-cols-[30%_70%] gap-8 scroll-mt-24">
      <div className="flex items-start gap-3 pt-0.5">
        <div className="flex flex-col items-center">
          <div className="size-2.5 rounded-full bg-gray-300 shrink-0 mt-1.5" />
          {!isLast && <div className="absolute top-4 w-px border-l border-dashed border-gray-300 h-[90%]" />}
        </div>
        <div className="flex flex-col gap-0.5">
          <h3 className="text-md font-semibold text-primary leading-snug">{update.title}</h3>
          {update.publishedAt && (
            <time className="text-xs text-quaternary font-medium">{formatShortDate(update.publishedAt)}</time>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 pb-12">
        {update.thumbnailUrl && (
          <img
            src={update.thumbnailUrl}
            alt={update.title}
            className="w-full rounded-lg object-contain max-w-[840px] mb-4"
          />
        )}
        <div className="flex flex-col gap-1.5">{renderCopy(update.copy)}</div>
        {update.linkUrl && (
          <div className="mt-2">
            <UpdateLink url={update.linkUrl} text={update.linkText || "Learn more"} merchantId={merchantId} />
          </div>
        )}
        {showDivider && <div className="border-b border-secondary mt-7" />}
      </div>
    </div>
  );
};

const TimelineSkeleton = () => (
  <div className="flex flex-col gap-6">
    {[1, 2, 3].map((i) => (
      <div key={i} className="grid grid-cols-[200px_1fr] gap-8">
        <div className="flex items-start gap-3">
          <div className="size-2.5 rounded-full bg-quaternary mt-1.5" />
          <div className="flex flex-col gap-1.5">
            <div className="h-4 w-32 animate-pulse rounded bg-quaternary" />
            <div className="h-3 w-16 animate-pulse rounded bg-quaternary" />
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="h-4 w-full animate-pulse rounded bg-quaternary" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-quaternary" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-quaternary" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
    <div className="flex size-12 items-center justify-center rounded-full bg-quaternary">
      <CheckVerified02 className="size-6 text-fg-quaternary" />
    </div>
    <div className="flex flex-col gap-1">
      <p className="text-md font-semibold text-primary">No updates yet</p>
      <p className="text-sm text-tertiary">Check back later for product announcements and updates.</p>
    </div>
  </div>
);

export const CustomerProductUpdatesPage = () => {
  const pagination = usePagination({ offset: 0, limit: 10 });
  const { data, isLoading } = useProductUpdates(pagination.pagination);
  const total = useCache(data?.total);
  const merchantId = useMerchant();
  const updates = data?.productUpdates ?? [];
  const totalPages = Math.ceil((total ?? 0) / pagination.pagination.limit);

  useEffect(() => {
    if (isLoading || updates.length === 0) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [isLoading, updates.length]);

  return (
    <div className="mx-auto flex w-full max-w-[1140px] flex-col gap-8 px-6 py-16 sm:px-12">
      <div className="flex items-center gap-3">
        <div className="flex flex-col">
          <h1 className="text-xl font-normal text-secondary flex flex-row items-center gap-2 mb-1">
            <CheckVerified02 className="w-5 h-5" strokeWidth={1.8} />
            Product Updates
          </h1>
          <span className="text-quaternary text-sm">What's new in Unbox</span>
        </div>
      </div>

      {isLoading ? (
        <TimelineSkeleton />
      ) : updates.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col">
          {updates.map((update, i) => (
            <TimelineEntry key={update.id} update={update} isLast={i === updates.length - 1} merchantId={merchantId} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <PaginationPageMinimalCenter
          page={pagination.page}
          total={totalPages}
          onPageChange={pagination.setPage}
          className="py-3"
        />
      )}
    </div>
  );
};
